import axios from 'axios';
import { parseStringPromise, Builder, processors } from 'xml2js';
import {
    GOALS,
    MAIN_CANVAS_ID,
    EDGE_TYPE,
    MARKETING_EMAIL_TYPES,
    GOAL_EMAIL_CONFIRM,
    DECISION_TYPE,
} from '../editor.constants';
import { utils } from '../legacy-utils';
import { findOutgoingCells } from './automation-edge-utils';
import {
    maxCellIdStore,
} from '../max-cell-id-utils';

const hasSomeValue = (object) => object && !!Object.keys(object).find((key) => (!!object[key]));

export const mapCampaignJsonToXml = ({ cells }) => {
    try {
        const builder = new Builder({
            headless: true, explicitArray: false, trim: true, tagNameProcessors: [processors.stripPrefix],
        });

        const fakeMxGraphModel = {
            mxGraphModel: {
                root: {
                    mxCell: [],
                },
            },
        };

        const root = cells[0];
        const main = cells[1];
        const otherCells = cells.filter((cell) => !['0', '1'].includes(`${cell.id}`));

        const { convertersThatRan = [] } = root.value;

        delete root.value.convertersThatRan;

        fakeMxGraphModel.mxGraphModel.root.mxCell = [
            {
                $: { ...root.meta },
                Object: {
                    $: { ...root.value },
                    Array: {
                        $: {
                            as: 'convertersThatRan',
                        },
                        add: Object.keys(convertersThatRan).map((key) => ({ $: { value: convertersThatRan[key] } })),
                    },
                },
            },
            {
                $: { ...main.meta },
            },
            ...otherCells.map((cell) => {
                const mxCell = { $: {} };

                if (hasSomeValue(cell.meta)) mxCell.$ = { ...cell.meta };

                if (hasSomeValue(cell.value)) {
                    mxCell.Object = {
                        ...mxCell.Object,
                        Array: [],
                        $: {},
                    };

                    mxCell.Object = { ...mxCell.Object };

                    Object.keys(cell.value).forEach((valueKey) => {
                        const cellValue = cell.value[valueKey];

                        if (Array.isArray(cellValue)) {
                            const arrayData = { $: { as: valueKey } };
                            const formattedArrayData = [];
                            const objectArrayData = [];

                            cellValue.forEach((arrayEntry) => {
                                if (typeof arrayEntry === 'object') {
                                    objectArrayData.push({
                                        $: { ...arrayEntry },
                                    });
                                } else formattedArrayData.push({ $: { value: arrayEntry } });
                            });

                            if (formattedArrayData.length) arrayData.add = formattedArrayData;
                            if (objectArrayData.length) arrayData.Object = objectArrayData;

                            mxCell.Object.Array.push(arrayData);
                        } else {
                            mxCell.Object.$[valueKey] = cellValue;
                        }
                    });
                }

                if (hasSomeValue(cell.mxGeometry)) mxCell.mxGeometry = { $: { ...cell.mxGeometry } };

                return mxCell;
            }),
        ];

        return builder.buildObject(fakeMxGraphModel);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);

        return null;
    }
};

export const sanitizeCells = (cells) => {
    const cellIds = cells.map((cell) => cell.id);

    return cells.filter((cell) => {
        if (cell.meta.parent && cell.meta.parent !== MAIN_CANVAS_ID && !cellIds.includes(cell.meta.parent)) {
            return false; // orphan sequence cells
        }

        if (cell.type === EDGE_TYPE) {
            // orphan edges
            if (!cellIds.includes(cell.meta.target)) return false;
            if (!cellIds.includes(cell.meta.source)) return false;
        }

        return true;
    });
};

export const getUpdatedRootCell = (rootCell, funnelId, appInfo) => {
    const updatedCell = { ...rootCell };
    const { appName, buildNumber } = appInfo;

    const formattedFunnelId = `${funnelId}L`;

    if (appName !== rootCell.value.appName || buildNumber !== rootCell.value.buildNumber || formattedFunnelId !== rootCell.value.funnelId) {
        updatedCell.value.appName = appName;
        updatedCell.value.buildNumber = buildNumber;
        updatedCell.value.funnelId = formattedFunnelId;
    }

    return updatedCell;
};

export const sanitizeDecisionCells = (sanitizedCells) => {
    const filteredDecisionCells = sanitizedCells.filter((cell) => cell.meta.style === DECISION_TYPE);

    for (const decision of filteredDecisionCells) {
        const flowsConnectedToDecisionDiamond = findOutgoingCells(decision.id, sanitizedCells);
        const flowIds = flowsConnectedToDecisionDiamond.map((cell) => cell.id);

        for (const decisionValue of decision.value.decisions) {
            if (!flowIds.includes(decisionValue.flowId)) {
                decisionValue.orphan = true;
            } else if (decisionValue.orphan) {
                delete decisionValue.orphan;
            }
        }
    }

    return sanitizedCells;
};

export const validatePoint = (point) => {
    point.x = Number.isNaN(point.x) || !point.x ? 0 : point.x;
    point.y = Number.isNaN(point.y) || !point.y ? 0 : point.y;

    return point;
};

export const transformAndSaveCampaign = async ({
    funnelId, draftJson, maxCellId, hash, appInfo,
}) => {
    const rootCell = getUpdatedRootCell(draftJson.cells[0], funnelId, appInfo);

    const sanitizedCells = sanitizeCells([rootCell, ...draftJson.cells.slice(1)]);
    const sanitizedDecisionCells = sanitizeDecisionCells(sanitizedCells);
    const xml = mapCampaignJsonToXml({ cells: sanitizedDecisionCells }).replaceAll("'", '&#39;');
    const data = { xml, maxCellId, hash };

    try {
        await axios.put(`/app/funnel/${funnelId}`, data);

        return xml;
    } catch (error) {
        if (error.response?.status === 409) {
            window.location = `/app/funnel/invalidHash?funnelId=${funnelId}`;
        }

        return new Error(`Could not save XML: ${error.response?.data}`);
    }
};

export const mapCampaignXmlToJson = async (campaignXml) => {
    const rawCampaignJson = campaignXml
        ? await parseStringPromise(campaignXml, { trim: true, explicitArray: true, tagNameProcessors: [processors.stripPrefix] })
        : {};
    let maxCellId = 0;

    const rawCells = rawCampaignJson.mxGraphModel?.root[0]?.mxCell || [];

    const valuesToParseAsNumbers = ['x', 'y', 'width', 'height'];

    const cells = rawCells.map((cell) => {
        const meta = cell.$ || {};

        const objectData = cell.Object && cell.Object[0]?.$ || {};
        const arrayData = cell.Object && cell.Object[0]?.Array;

        let mxGeometry = cell.mxGeometry && cell.mxGeometry[0]?.$ || {};

        mxGeometry = validatePoint(mxGeometry);

        valuesToParseAsNumbers.forEach((value) => {
            if (mxGeometry[value]) {
                mxGeometry[value] = Number.parseInt(mxGeometry[value], 10);
            } else {
                delete mxGeometry[value];
            }
        });

        let type = meta.metaType || meta.style;

        if (meta.style === GOAL_EMAIL_CONFIRM) type = GOAL_EMAIL_CONFIRM;

        const cellObject = {
            meta,
            id: meta.id,
            type,
            value: objectData,
            mxGeometry,
        };

        const cellObjectId = parseInt(cellObject.id, 10);

        if (cellObjectId > maxCellId) {
            maxCellId = cellObjectId;
        }

        if (arrayData) {
            arrayData.forEach((arrayDataItem) => {
                const fieldKey = arrayDataItem?.$?.as;

                if (!fieldKey) return;

                if (arrayDataItem.Object || arrayDataItem.add) {
                    if (arrayDataItem.Object) {
                        const valueKey = arrayDataItem.$.as;
                        const valueArray = arrayDataItem.Object.map((item) => {
                            return item.$;
                        });

                        cellObject.value[valueKey] = valueArray;
                    }

                    if (arrayDataItem.add) {
                        const valueArray = arrayDataItem.add.map((item) => (item.$?.value));

                        cellObject.value[fieldKey] = valueArray;
                    }
                } else {
                    cellObject.value[fieldKey] = [];
                }
            });
        }

        if (maxCellId > maxCellIdStore.maxCellId) {
            maxCellIdStore.setCount(maxCellId);
        }

        return cellObject;
    });

    return { cells };
};

export const generateEmptyCampaignCells = (funnelId, { appName, buildNumber }) => {
    return [{
        id: '0',
        meta: {
            id: '0',
        },
        mxGeometry: {},
        type: undefined,
        text: '',
        value: {
            appName,
            as: 'value',
            buildNumber,
            funnelId,
            initialized: '1',
            convertersThatRan: [],
            add: [],
        },
    },
    {
        id: '1',
        meta: {
            id: '1',
            parent: '0',
        },
        mxGeometry: {},
        type: undefined,
        text: '',
        value: {},
    }];
};


export const executeOnAllMatchingEmails = (marketingEmailIds, draftJson, callback) => {
    draftJson.cells.forEach((cell) => {
        if (MARKETING_EMAIL_TYPES.includes(cell.type)) {
            marketingEmailIds.forEach((marketingEmailId) => {
                if (marketingEmailId && utils.removeLongSuffix(cell.value?.marketingEmailId) === marketingEmailId.toString()) {
                    callback(cell.value.name);
                }
            });
        }
    });
};

export const revertAdditionalObjectsIfNeeded = (draftJson, revertedJson) => {
    const draftInternalForms = {};
    const draftWebForms = {};
    const draftLandingPages = {};

    draftJson.cells.forEach((cell) => {
        if (cell.type === GOALS.GOAL_INTERNAL_FORM && cell.value.internalFormId && cell.value.published === '0') {
            const id = cell.value.internalFormId.replace('L', '');

            draftInternalForms[id] = { ...cell };
        } else if (cell.type === GOALS.GOAL_WEB_FORM && cell.value.webformId && cell.value.published === '0') {
            const id = cell.value.webformId.replace('L', '');

            draftWebForms[id] = { ...cell };
        } else if (cell.type === GOALS.GOAL_UNLAYER_LANDING_PAGE && cell.value.landingPageId && cell.value.published === '0') {
            const id = cell.value.landingPageId.replace('L', '');

            draftLandingPages[id] = { ...cell };
        }
    });

    let hasChanges = false;

    const updatedCells = revertedJson.cells.map((cell) => {
        if (cell.type === GOALS.GOAL_INTERNAL_FORM && cell.value.internalFormId) {
            const id = cell.value.internalFormId.replace('L', '');

            if (draftInternalForms[id] && cell.value.published === '1') {
                hasChanges = true;
                const updatedCell = { ...cell };

                updatedCell.value.published = '0';

                return updatedCell;
            }
        } else if (cell.type === GOALS.GOAL_WEB_FORM && cell.value.webformId) {
            const id = cell.value.webformId.replace('L', '');

            if (draftWebForms[id] && cell.value.published === '1') {
                hasChanges = true;
                const updatedCell = { ...cell };

                updatedCell.value.published = '0';

                return updatedCell;
            }
        } else if (cell.type === GOALS.GOAL_UNLAYER_LANDING_PAGE && cell.value.landingPageId) {
            const id = cell.value.landingPageId.replace('L', '');

            if (draftLandingPages[id] && cell.value.published === '1') {
                hasChanges = true;
                const updatedCell = { ...cell };

                updatedCell.value.published = '0';

                return updatedCell;
            }
        }

        return cell;
    });

    return { hasChanges, updatedCells };
};
